<template>
  <div class="book-exercise">
    <div class="book-exercise__header">
      <div class="book-exercise__tag">
        <div class="iconfont ipen book-exercise__tag-icon"></div>
        <p class="book-exercise__tag-text">Exercise</p>
      </div>

      <div class="book-exercise__banner">
        <h2 class="book-exercise__title">{{ exerciseTitle }}</h2>
        <div
          class="book-exercise__description"
          oncopy="return false"
          v-html="exercise.text"
        ></div>
      </div>
    </div>

    <div class="book-exercise__body">
      <div
        v-for="(answer, index) in answers"
        :key="answer.questionId"
        class="book-exercise__question"
      >
        <div class="book-exercise__label-wrapper">
          <div class="book-exercise__question-number-wrapper">
            <div class="book-exercise__question-number">
              {{ index + 1 }}
            </div>
          </div>
          <label
            class="book-exercise__label"
            :for="answer.questionId"
            oncopy="return false"
            v-html="answer.questionText"
          ></label>
        </div>
        <InputWithContextMenu
          :id="answer.questionId"
          v-model="answer.answer"
          elementType="textarea"
          class="book-exercise__input"
          :disabled="readOnlyMode"
          placeholder="Type your answer here."
          rows="1"
          :autoResize="true"
          @input="onChange()"
        />
      </div>
    </div>

    <div v-if="!isEOC" class="book-exercise__controls">
      <div class="book-exercise__buttons">
        <AppButton
          v-if="readOnlyMode"
          class="book-exercise__btn book-exercise__btn--edit"
          icon="ipen"
          @click="switchToEdit()"
        >
          Edit
        </AppButton>
        <AppButton
          v-else
          class="book-exercise__btn book-exercise__btn--submit"
          :loading="wait.is('Submitting')"
          :disabled="wait.is('Submitting')"
          @click="submit()"
        >
          Save Answers
        </AppButton>
        <AppButton
          v-if="exercise.submitted_at"
          class="book-exercise__btn book-exercise__btn--discuss"
          icon="icategory-communication"
          :outlined="true"
          :loading="wait.is('Sending')"
          :disabled="wait.is('Sending')"
          @click="discuss()"
        >
          Discuss
        </AppButton>
      </div>
      <div v-show="validationState === false" class="book-exercise__error">
        <strong>Error:</strong> {{ errorRef }}
      </div>
    </div>

    <div
      v-if="!isEOC && exercise.threads && exercise.threads.length > 0"
      class="book-exercise__threads"
    >
      <h3 class="book-exercise__threads-title">
        Discussions You Posted for This Exercise
      </h3>

      <router-link
        v-for="thread in exercise.threads"
        :key="thread.id"
        :to="{
          name: content.book.routeNames().thread,
          params: { thread: thread.url_slug },
        }"
        class="book-exercise__thread"
      >
        <p class="book-exercise__thread-title">
          {{ thread.title }}
          <span class="book-exercise__reply-count">
            {{ `- ${thread.comments_count} replies` }}
          </span>
        </p>

        <p class="book-exercise__thread-date">
          {{ formatTime(thread.created) }}
        </p>
      </router-link>
    </div>

    <EOCButton
      v-if="isEOC"
      text="Start Free Trial to Unlock"
      ios-text="Full Subscription Required to Access Exercise"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import type { Ref } from 'vue'
import { useRouter } from 'vue-router'
import { Sentry } from '@/services/sentry'

import type { ExerciseView, OpenQuestionsView } from '@/models/doc.book'
import { convertError } from '@/models/error'
import { formatTrialDate } from '@/helpers/time'
import { useWait } from '@/app/composables/useWait'
import { assertNotNull } from '@/helpers/typing'
import { Mixpanel } from '@/analytics/mixpanel'

import InputWithContextMenu from '@/app/components/InputWithContextMenu.vue'
import AppButton from '@/app/components/button/AppButton.vue'
import EOCButton from '@/app/components/billing/EOCButton.vue'

const props = defineProps<{
  content: OpenQuestionsView
  isEOC: boolean
}>()

const wait = useWait()
const router = useRouter()

const timeoutId: Ref<number | undefined> = ref(undefined)
const isSaving = ref(false)
const editSubmitted = ref(false)
const errorRef: Ref<string | null> = ref(null)

// For tests, to permanently disable timers,
// can be set by `disableTimers()` method call.
const timersDisabled = ref(false)
const trackedExercises = new WeakSet<ExerciseView>()

const exercise = computed(() => {
  return assertNotNull(props.content).currentExercise
})

// Navigating between exercises updates this value without recreating the
// component. Save any answer still waiting for autosave, clear the previous
// form state, and allow the newly displayed exercise's first save to be tracked.
watch(exercise, async (newExercise, previousExercise) => {
  const hasPendingAutosave = timeoutId.value !== undefined
  clearTimeout(timeoutId.value)
  timeoutId.value = undefined
  isSaving.value = false
  editSubmitted.value = false
  errorRef.value = null
  trackedExercises.delete(newExercise)

  wait.end('Submitting')
  wait.end('Autosaving')
  wait.end('Sending')

  if (hasPendingAutosave) {
    try {
      await previousExercise.save()
      trackAnswerSaved(previousExercise)
    } catch (error) {
      Sentry.captureException(error)
    }
  }
})

const exerciseTitle = computed(() => {
  return exercise.value.title.replace('Exercise: ', '')
})

const answers = computed(() => {
  if (props.isEOC) {
    return exercise.value.answers.slice(0, 1)
  }

  return exercise.value.answers
})

const readOnlyMode = computed(() => {
  return props.isEOC || (!!exercise.value.submitted_at && !editSubmitted.value)
})

const validationState = computed(() => {
  if (errorRef.value) {
    return false
  }

  return null
})

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function disableTimers(): void {
  timersDisabled.value = true
}

function switchToEdit(): void {
  editSubmitted.value = true
}

function formatTime(time: string): string {
  return formatTrialDate(time)
}

function trackAnswerSaved(savedExercise: ExerciseView): void {
  if (
    trackedExercises.has(savedExercise) ||
    !savedExercise.answers.some((answer) => answer.answer?.trim())
  ) {
    return
  }

  trackedExercises.add(savedExercise)
  Mixpanel.trackExerciseAnswerSaved(
    savedExercise.book.id,
    savedExercise.content_id,
  )
}

/**
 * Auto-saves exercise data as the user enters it.
 */
async function onChange(): Promise<void> {
  const exerciseToSave = exercise.value
  errorRef.value = null
  // If a timer was already started, clear it.
  if (timeoutId.value || isSaving.value) {
    clearTimeout(timeoutId.value)
  }

  if (timersDisabled.value) {
    return
  }

  // Set timer that will save comment when it fires.
  timeoutId.value = setTimeout(async () => {
    timeoutId.value = undefined
    // Make ajax call to save data.
    isSaving.value = true
    // TODO: Add UI for autosaving
    wait.start('Autosaving')
    try {
      await exerciseToSave.save()
      trackAnswerSaved(exerciseToSave)
    } finally {
      if (exercise.value === exerciseToSave) {
        wait.end('Autosaving')
        isSaving.value = false
      }
    }
  }, 750)
}

async function submit(): Promise<void> {
  const exerciseToSave = exercise.value
  isSaving.value = true
  errorRef.value = null
  try {
    wait.start('Submitting')
    await exerciseToSave.submit()
    trackAnswerSaved(exerciseToSave)
  } catch (error: any) {
    const err = convertError(error)
    if (err.isValidationError()) {
      errorRef.value = err.getOne('answers')
    } else {
      Sentry.captureException(error)
      throw error
    }
  } finally {
    wait.end('Submitting')
    isSaving.value = false
  }
  editSubmitted.value = false
}

async function discuss(): Promise<void> {
  errorRef.value = null

  try {
    wait.start('Sending')
    const thread: any = await exercise.value.discuss()
    router.push({
      name: 'book.edit_thread',
      state: {
        threadTitle: thread.title,
        threadText: thread.text,
        userWorkoutId: thread.user_workout_id,
      },
    })
  } finally {
    wait.end('Sending')
  }
}
</script>

<style lang="scss" scoped>
.book-exercise {
  display: flex;
  flex-direction: column;

  p:last-child,
  :deep(p:last-child) {
    margin: 0;
  }

  &__tag {
    display: flex;
    gap: 4px;
    align-items: center;
    margin-bottom: 12px;
  }

  &__tag-icon {
    width: 24px;
    height: 24px;
    font-size: 24px;
    line-height: 1;
    margin-top: -4px;
    color: $c-blue-4;
  }

  &__tag-text {
    margin: 0;
    font-size: 18px;
    line-height: 1;
    color: $c-blue-4;
  }

  &__banner {
    padding: 40px;
    border-radius: 8px;
    background: rgba($c-blue-5, 0.1);
    margin-bottom: 40px;
  }

  &__title {
    margin-bottom: 8px;
  }

  &__description {
    font-weight: 400;
    color: $c-black-3;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 40px;
    margin-bottom: 48px;
  }

  &__label-wrapper {
    display: flex;
    gap: 16px;
    margin-bottom: 12px;
    @media only screen and (max-width: $w-mobile-s) {
      flex-direction: column;
      gap: 4px;
    }
  }

  &__label {
    font-weight: 500;
  }

  &__question-number-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 1.56em;
    height: 1.56em;
    border-radius: 999px;
    border: 1px solid $c-black-13;
    background: $c-bg;
  }

  &__question-number {
    font-size: 0.78em;
    line-height: 1;
    letter-spacing: 0;
    margin-bottom: -0.3em;
  }

  &__input {
    width: calc(100% - 1.56em - 16px);
    margin-left: calc(1.56em + 16px);
    padding-block: 8px;
    border-bottom: 1px solid $c-black-13;
    background: transparent;
    @media only screen and (max-width: $w-mobile-s) {
      width: 100%;
      margin-left: 0;
    }

    &:not([disabled]) {
      @include hover {
        border-color: $c-black-3;
      }

      &:focus {
        border-color: $c-yellow;
      }
    }

    &[disabled] {
      border-color: transparent;
    }
  }

  &__controls {
    margin-left: calc(1.56em + 16px);
    margin-bottom: 64px;
    @media only screen and (max-width: $w-mobile-s) {
      margin-left: 0;
    }
  }

  &__buttons {
    display: flex;
    gap: 24px;
    @media only screen and (max-width: $w-mobile-s) {
      gap: 16px;
    }
  }

  &__btn {
    font-size: 18px;
    padding-block: 7px;
    @media only screen and (max-width: $w-mobile-s) {
      font-size: 16px;
      flex: 1;
    }
  }

  &__btn--edit :deep(.iconfont) {
    margin-right: 4px;
  }

  &__error {
    margin-top: 0.5em;
    color: $c-red;

    strong {
      font-weight: 500;
    }
  }

  &__threads-title {
    padding: 0;
    margin: 0;
    border: none;
    margin-bottom: 12px;
  }

  &__thread {
    display: block;
    padding: 20px 40px;
    border-radius: 8px;
    text-decoration: none;
    border: 1px solid transparent;
    background: $c-bg;
    @media only screen and (max-width: $w-mobile-s) {
      padding: 10px 20px;
    }

    @include hover {
      text-decoration: none;
      border-color: $c-black-13;
    }
  }

  &__thread-title {
    margin-bottom: 0;
    font-weight: 500;
    text-decoration: none;
    color: $c-black-2;
  }

  &__reply-count {
    color: $c-black-3;
  }

  &__thread-date {
    font-size: 0.9em;
    font-weight: 400;
    color: $c-black-3;
  }
}

.night-mode .book-exercise {
  &__description {
    color: $c-dark-7;
  }

  &__question-number-wrapper {
    background: $c-black-8;
    border-color: $c-border;
  }

  &__input {
    color: $c-dark-4;
    border-color: $c-black-8;

    &:not([disabled]) {
      @include hover {
        border-color: $c-border-6;
      }

      &:focus {
        border-color: $c-yellow;
      }
    }

    &[disabled] {
      color: $c-dark-8;
      border-color: transparent;
    }
  }

  &__btn--discuss {
    background: $c-dark-2;
  }

  &__thread {
    background: $c-black-8;

    @include hover {
      border-color: $c-border-6;
    }
  }

  &__thread-title {
    color: $c-dark-4;
  }

  &__reply-count,
  &__thread-date {
    color: $c-dark-7;
  }
}
</style>
